import PropTypes from 'prop-types'
import { useMemo } from 'react'
import styled, { css } from 'styled-components/macro'

import { IconCalendar } from '@loadsmart/icons'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { formatZonedDate } from 'legacy/utils/date/formatZonedDate'
import { APPT_PENDING, getStopInfo } from 'legacy/utils/getStopInfo'

import { Description, Label, Section } from './Section'

const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;

  color: ${getToken('color-neutral-darkest')};
  font-weight: 400;
  font-size: 13px;

  > :not(:last-child) {
    margin-right: 5px;
  }
`

const DateBox = styled.span`
  ${({ strongDate }) =>
    strongDate &&
    `
      font-weight: bold;
    `};
`

const StyledIconCalendar = styled(IconCalendar)`
  width: 16px;
  height: 16px;
  margin-right: 5px;
`

const Time = styled.span`
  ${props =>
    props.highlight &&
    css`
      color: ${getToken('color-success')};
      font-weight: ${getToken('font-weight-bold')};
    `};
`

function Appointment(props) {
  const {
    appointment,
    showDate,
    showTime,
    strongDate,
    showIcon,
    highlight,
    includeLabel,
    ...rest
  } = props

  let content = null

  const { window_start, window_end, timezone } = appointment
  const isAppointmentWindow = useMemo(() => window_start !== window_end, [window_start, window_end])
  const { appointmentLabel } = getStopInfo({ stop: appointment })
  const formattedDate = useMemo(
    () => formatZonedDate(window_start, timezone, 'EEE, MMM d'),
    [timezone, window_start]
  )

  const formattedTime = useMemo(
    () => formatZonedDate(window_start, timezone, 'HH:mm'),
    [timezone, window_start]
  )

  content = (
    <Wrapper {...rest}>
      {showIcon && <StyledIconCalendar width={16} height={16} />}
      {showTime && <Time highlight={highlight}>{formattedTime}</Time>}

      {isAppointmentWindow && showTime && (
        <>
          <span>-</span>
          <span>{formatZonedDate(window_end, timezone, 'HH:mm')}</span>
        </>
      )}

      {showDate && <DateBox strongDate={strongDate}>{formattedDate}</DateBox>}
    </Wrapper>
  )

  if (appointmentLabel === APPT_PENDING) {
    return <Label>{appointmentLabel}</Label>
  }

  if (includeLabel) {
    return (
      <Section>
        <Label>{appointmentLabel}</Label>
        <Description>{content}</Description>
      </Section>
    )
  }

  return content
}

Appointment.propTypes = {
  appointment: PropTypes.shape({
    window_start: PropTypes.string.isRequired,
    window_end: PropTypes.string.isRequired,
    timezone: PropTypes.string,
  }).isRequired,
  showDate: PropTypes.bool,
  showTime: PropTypes.bool,
  showIcon: PropTypes.bool,
  strongDate: PropTypes.bool,
  includeLabel: PropTypes.bool,
  highlight: PropTypes.bool,
}

Appointment.defaultProps = {
  showDate: true,
  showTime: true,
  showIcon: false,
  strongDate: false,
  includeLabel: false,
  highlight: false,
}

export { Appointment }
